import { supabase } from './client';
import { authStore } from '@stores/session';
import type { PortfolioSymptom } from './portfolio';
import type { StrainRow } from './queries';
import { fetchStrainSymptoms } from './strainSymptoms';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = supabase as any;

// Phase E repurposes user_watchlist: the same table now backs both
// "I want this in my next prescription" and the legacy "notify me when
// it's back in stock" — the notify behaviour is just a boolean toggle.
//
// The restock-alert cron (migrations 0008/earlier) continues to read
// `notify_on_restock = true` rows, so existing alert UX is preserved.
//
// Symptom tags live in user_strain_symptoms (migration 0049) keyed by
// (profile, strain), so they're shared with /my-strains and the listing
// pages and persist regardless of which list the strain is in. We fetch
// the user's strain-symptom map and merge it in by strain_id rather than
// joining a list-scoped table. move_watch_to_portfolio() no longer needs
// to copy tags — they already belong to the user+strain.

const SELECT_COLUMNS =
  'id, strain_id, notify_on_restock, is_in_next_script, script_quantity, created_at, ' +
  'strain:strains(id, name, brand, genetics, category, slug, lineage)';

type WatchRow = {
  id: string;
  strain_id: string;
  notify_on_restock: boolean;
  is_in_next_script: boolean;
  script_quantity: number;
  created_at: string;
  strain: StrainRow;
};

export type WatchEntry = {
  id: string;
  strain_id: string;
  notify_on_restock: boolean;
  // Migration 0045 — Prescription Planner staging fields.
  is_in_next_script: boolean;
  script_quantity: number;
  created_at: string;
  strain: StrainRow;
  symptoms: PortfolioSymptom[];
};

function mapWatchRow(
  row: WatchRow,
  symptoms: PortfolioSymptom[],
): WatchEntry {
  return {
    id: row.id,
    strain_id: row.strain_id,
    notify_on_restock: row.notify_on_restock,
    is_in_next_script: row.is_in_next_script,
    script_quantity: row.script_quantity,
    created_at: row.created_at,
    strain: row.strain,
    symptoms,
  };
}

export async function fetchWatchlist(): Promise<WatchEntry[]> {
  const profile = authStore.profile();
  if (!profile) return [];
  const { data, error } = await db
    .from('user_watchlist')
    .select(SELECT_COLUMNS)
    .eq('profile_id', profile.id)
    .order('created_at', { ascending: false });
  if (error) throw error;
  const symptomMap = await fetchStrainSymptoms();
  return ((data ?? []) as WatchRow[]).map((row) =>
    mapWatchRow(row, symptomMap.get(row.strain_id) ?? []),
  );
}

export async function isWatched(strainId: string): Promise<boolean> {
  const profile = authStore.profile();
  if (!profile) return false;
  const { data, error } = await supabase
    .from('user_watchlist')
    .select('id')
    .eq('profile_id', profile.id)
    .eq('strain_id', strainId)
    .maybeSingle();
  if (error) throw error;
  return data != null;
}

/**
 * Bulk membership lookup for the listing pages — counterpart to
 * fetchPortfolioStrainIds. One query feeds the "+ Watchlist" quick-
 * action across every card on /strains and /stock.
 */
export async function fetchWatchedStrainIds(): Promise<Set<string>> {
  const profile = authStore.profile();
  if (!profile) return new Set();
  const { data, error } = await supabase
    .from('user_watchlist')
    .select('strain_id')
    .eq('profile_id', profile.id);
  if (error) throw error;
  return new Set(((data ?? []) as { strain_id: string }[]).map((r) => r.strain_id));
}

export async function fetchWatchEntry(strainId: string): Promise<WatchEntry | null> {
  const profile = authStore.profile();
  if (!profile) return null;
  const { data, error } = await db
    .from('user_watchlist')
    .select(SELECT_COLUMNS)
    .eq('profile_id', profile.id)
    .eq('strain_id', strainId)
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  const symptomMap = await fetchStrainSymptoms();
  return mapWatchRow(data as WatchRow, symptomMap.get(strainId) ?? []);
}

export async function addWatch(strainId: string, notifyOnRestock = true): Promise<void> {
  const profile = authStore.profile();
  if (!profile) throw new Error('Sign in required');
  const { error } = await supabase.from('user_watchlist').insert({
    profile_id: profile.id,
    strain_id: strainId,
    notify_on_restock: notifyOnRestock,
  });
  if (error && error.code !== '23505') throw error;
}

export async function removeWatch(strainId: string): Promise<void> {
  const profile = authStore.profile();
  if (!profile) throw new Error('Sign in required');
  const { error } = await supabase
    .from('user_watchlist')
    .delete()
    .eq('profile_id', profile.id)
    .eq('strain_id', strainId);
  if (error) throw error;
}

export async function setNotifyOnRestock(
  strainId: string,
  notify: boolean,
): Promise<void> {
  const profile = authStore.profile();
  if (!profile) throw new Error('Sign in required');
  const { error } = await supabase
    .from('user_watchlist')
    .update({ notify_on_restock: notify })
    .eq('profile_id', profile.id)
    .eq('strain_id', strainId);
  if (error) throw error;
}

/**
 * Atomically move a watchlist entry into the user's portfolio. Calls
 * the move_watch_to_portfolio() RPC (rewritten in migration 0049) which:
 *   1. Upserts the user_portfolio row (preserving any existing entry).
 *   2. Deletes the user_watchlist row.
 *
 * Symptom tags need no carry-over — they live in user_strain_symptoms
 * keyed by (profile, strain), so they already follow the strain across
 * lists. Returns the resulting portfolio entry id. Errors propagate so the
 * caller can surface a message and the local optimistic prune doesn't
 * fire.
 */
export async function moveToPortfolio(strainId: string): Promise<string> {
  const profile = authStore.profile();
  if (!profile) throw new Error('Sign in required');
  const { data, error } = await db.rpc('move_watch_to_portfolio', {
    p_strain_id: strainId,
  });
  if (error) throw error;
  return data as string;
}

// ---------------------------------------------------------------------------
// Prescription Planner mutations (migration 0045).
// ---------------------------------------------------------------------------

/**
 * Toggle the Next-Script flag on a single watchlist entry. Bumps
 * script_quantity to 1 when adding (the default per the migration's
 * column default) and resets it to 1 on remove so a future re-promote
 * doesn't inherit a stale count.
 */
export async function setNextScript(strainId: string, on: boolean): Promise<void> {
  const profile = authStore.profile();
  if (!profile) throw new Error('Sign in required');
  const patch = on
    ? { is_in_next_script: true }
    : { is_in_next_script: false, script_quantity: 1 };
  const { error } = await db
    .from('user_watchlist')
    .update(patch)
    .eq('profile_id', profile.id)
    .eq('strain_id', strainId);
  if (error) throw error;
}

/**
 * Set the per-row quantity for a Next-Script item. The DB CHECK
 * constraint enforces 1..99, so the client clamps before calling to
 * give a friendlier error than the raw Postgres constraint message.
 */
export async function setScriptQuantity(
  strainId: string,
  quantity: number,
): Promise<void> {
  const profile = authStore.profile();
  if (!profile) throw new Error('Sign in required');
  const clamped = Math.max(1, Math.min(99, Math.round(quantity)));
  const { error } = await db
    .from('user_watchlist')
    .update({ script_quantity: clamped })
    .eq('profile_id', profile.id)
    .eq('strain_id', strainId);
  if (error) throw error;
}

/**
 * Clear the entire Next-Script. Server-side via the clear_next_script
 * RPC (migration 0045) so we get the count returned for an optional
 * "cleared 4 items" toast.
 */
export async function clearNextScript(): Promise<number> {
  const profile = authStore.profile();
  if (!profile) throw new Error('Sign in required');
  const { data, error } = await db.rpc('clear_next_script');
  if (error) throw error;
  return (data as number) ?? 0;
}

/**
 * Move every Next-Script entry into the user's portfolio in one
 * transaction. Symptom tags carry over; the watchlist rows are
 * deleted. Returns the count moved.
 */
export async function batchMoveScriptToPortfolio(): Promise<number> {
  const profile = authStore.profile();
  if (!profile) throw new Error('Sign in required');
  const { data, error } = await db.rpc('batch_move_script_to_portfolio');
  if (error) throw error;
  return (data as number) ?? 0;
}

/**
 * Bulk-add a set of strain ids to the user's watchlist. Used by the
 * /shared-script receiver flow's "Add all to my Watchlist" action.
 * ON CONFLICT DO NOTHING so already-watched strains are no-ops
 * rather than errors — relies on the unique (profile_id, strain_id)
 * constraint added in migration 0045.
 *
 * Optionally promotes every inserted row into the Next Script in one
 * pass — used by "Add all to my Next Script" so the receiver doesn't
 * have to flip a toggle on each strain after import.
 */
export async function bulkAddToWatchlist(
  strainIds: string[],
  options?: { addToNextScript?: boolean },
): Promise<void> {
  const profile = authStore.profile();
  if (!profile) throw new Error('Sign in required');
  if (strainIds.length === 0) return;

  const rows = strainIds.map((strain_id) => ({
    profile_id: profile.id,
    strain_id,
    is_in_next_script: options?.addToNextScript ?? false,
  }));

  // upsert with ignoreDuplicates is the supabase-js shape for
  // INSERT ... ON CONFLICT DO NOTHING. The unique-constraint name from
  // migration 0045 is the conflict target.
  const insert = await db
    .from('user_watchlist')
    .upsert(rows, {
      onConflict: 'profile_id,strain_id',
      ignoreDuplicates: true,
    });
  if (insert.error) throw insert.error;

  // Idempotent flag-flip for strains that were already in the
  // watchlist when "Add to Next Script" was clicked. Without this
  // step they'd be silently skipped above and the user wouldn't see
  // them appear in the Next Script section.
  if (options?.addToNextScript) {
    const { error } = await db
      .from('user_watchlist')
      .update({ is_in_next_script: true })
      .eq('profile_id', profile.id)
      .in('strain_id', strainIds);
    if (error) throw error;
  }
}
